import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import { $getNodeByKey, $isTextNode, COMMAND_PRIORITY_NORMAL, FORMAT_TEXT_COMMAND, ParagraphNode } from 'lexical';
import { useEffect } from 'react';
import { SupportedFeatures, SupportedTextFormatType } from '../../../utils/features';

export interface DisableFeaturesFunctionPluginProps {
    supportedFeatures: SupportedFeatures[];
}

const supportedTextFormat: SupportedTextFormatType[] = ['bold', 'italic', 'underline'];

const DisableFeaturesFunctionPlugin = ({ supportedFeatures }: DisableFeaturesFunctionPluginProps) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return mergeRegister(
            // formats (ctrl+b, ctrl+i, ...etc)
            editor.registerCommand(
                FORMAT_TEXT_COMMAND,
                (type) => !supportedFeatures.includes(type as SupportedFeatures),
                COMMAND_PRIORITY_NORMAL,
            ),

            editor.registerUpdateListener((listener) => {
                // https://github.com/facebook/lexical/discussions/4779 (unanswered, listen to paste command, but after applied in editor)
                if (!listener.tags.has('paste')) return;

                editor.update(
                    () => {
                        listener.dirtyLeaves.forEach((key) => {
                            const node = $getNodeByKey(key);
                            if (!node) return;

                            if ($isTextNode(node)) {
                                supportedTextFormat.forEach((textFormat) => {
                                    if (node.hasFormat(textFormat) && !supportedFeatures.includes(textFormat)) {
                                        node.toggleFormat(textFormat);
                                    }
                                });
                            }
                        });
                    },
                    // change is kept/pushed into stack by default, tell LexicalHistory to merge this update instead
                    { tag: 'history-merge' },
                );
            }),

            // list
            editor.registerNodeTransform(ListItemNode, (node) => {
                const listNode = node.getParent() as ListNode;

                const disableOl = !supportedFeatures?.includes('ol');
                const disableUl = !supportedFeatures?.includes('ul');
                const isOl = listNode.getListType() === 'number';
                const isUl = listNode.getListType() === 'bullet' || listNode.getListType() === 'check';

                if (disableOl && isOl) {
                    node.replace(new ParagraphNode(), true);
                }

                if (disableUl && isUl) {
                    node.replace(new ParagraphNode(), true);
                }
            }),

            editor.registerNodeTransform(
                HeadingNode,
                (node) => !supportedFeatures.includes('headingStyle') && node.replace(new ParagraphNode(), true),
            ),

            editor.registerNodeTransform(
                LinkNode,
                (node) => !supportedFeatures.includes('link') && node.replace(new ParagraphNode(), true),
            ),
        );
    }, [supportedFeatures, editor]);

    return null;
};

export default DisableFeaturesFunctionPlugin;
